import { computed, ref, type Ref, type WritableComputedRef } from 'vue'

import type { AgreementOpeningLookup } from './AgreementOpeningLookup'
import type { AgreementOpeningState } from './AgreementOpeningState'

interface AgreementOpeningCollaborators<A, P extends unknown[]> {
  /** Reads the agreement to open. */
  load: (...args: P) => Promise<AgreementOpeningLookup<A>>
  /** Reports an agreement that could not be loaded; the agreement stays closed. */
  onLoadFailure: (error: unknown) => void
  /** Reports that there is no agreement to open; the agreement stays closed. */
  onAbsent: () => void
}

interface AgreementOpening<A, P extends unknown[]> {
  close: () => void
  isOpen: WritableComputedRef<boolean>
  open: (...args: P) => Promise<void>
  state: Ref<AgreementOpeningState<A>>
}

/** Loads an agreement and opens it in place; a newer open or a close discards any load in flight. */
export const useAgreementOpening = <A, P extends unknown[] = []>(
  collaborators: AgreementOpeningCollaborators<A, P>
): AgreementOpening<A, P> => {
  const state = ref<AgreementOpeningState<A>>({ isOpen: false }) as Ref<AgreementOpeningState<A>>
  let generation = 0

  const close = (): void => {
    generation++
    state.value = { isOpen: false }
  }

  const isOpen = computed({
    get: () => state.value.isOpen,
    set: (open: boolean) => {
      if (!open) close()
    }
  })

  const open = async (...args: P): Promise<void> => {
    const request = ++generation
    try {
      const lookup = await collaborators.load(...args)
      if (request !== generation) return
      if (lookup.isFound) state.value = { agreement: lookup.agreement, isOpen: true }
      else collaborators.onAbsent()
    } catch (error: unknown) {
      if (request !== generation) return
      collaborators.onLoadFailure(error)
    }
  }

  return { close, isOpen, open, state }
}
